import React, { useEffect, useRef, useState } from "react";
import { StageCarouselSlide } from "~assets/frontend/modules/mod-stage_carousel/stage_carousel_slide";
import { Icon } from "~assets/frontend/elements/icon/Icon";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { Indicators } from "~assets/frontend/elements/indicator/indicator";
import { useSwipe } from "~assets/frontend/_util/hooks/useSwipe";
import { dispatchTrackingEvent } from "~assets/global/tracking/tracking-event";
import { useWindowFocus } from "~assets/frontend/_util/hooks/useWindowFocus";
import { usePreventScroll } from "~assets/frontend/_util/hooks/usePreventScroll";
import { getId } from "~assets/frontend/_util/getId";
const totalAnimationDuration = 2;
const slideAnimationDuration = 1.5;
export const StageCarousel = ({ slides, interval, headline_tag, headline_appearance, inpage_nav_title }) => {
    const [timer, setTimer] = useState(undefined);
    const [intervalId, setIntervalId] = useState(undefined);
    const [mousePosition, setMousePosition] = useState(null);
    const [navigationDirection, setNavigationDirection] = useState("next");
    const [currentSlide, setCurrentSlide] = useState(0);
    const [disableClickNavigation, setDisableClickNavigation] = useState(false);
    const windowFocus = useWindowFocus();
    const preventScrolling = usePreventScroll();
    useEffect(() => {
        if (!preventScrolling && windowFocus && interval > 0 && intervalId === undefined) {
            setIntervalId(window.setInterval(() => {
                navigateNext("auto_next");
            }, interval * 1000));
            return () => {
                clearInterval(intervalId);
                setIntervalId(undefined);
            };
        }
        else {
            clearInterval(intervalId);
        }
    }, [interval, windowFocus, preventScrolling]);
    const childWrapperRef = useRef(null);
    const temporarilyDisableClickNavigation = () => {
        setDisableClickNavigation(true);
        setTimeout(() => {
            setMousePosition(null);
            setDisableClickNavigation(false);
        }, totalAnimationDuration * 1000);
    };
    const navigateNext = (ui_action) => {
        temporarilyDisableClickNavigation();
        setNavigationDirection("next");
        if (ui_action) {
            dispatchTrackingEvent({
                name: "ui",
                payload: {
                    ui_function: "carousel",
                    ui_action: ui_action,
                    ui_component: "mod-stage_carousel",
                    ui_target: "slide " + (currentSlide + 1 > slides.length - 1 ? 0 : currentSlide + 1),
                },
            });
        }
        setCurrentSlide((currentState) => (currentState + 1 > slides.length - 1 ? 0 : currentState + 1));
    };
    const navigatePrev = (ui_action) => {
        temporarilyDisableClickNavigation();
        setNavigationDirection("prev");
        if (ui_action) {
            dispatchTrackingEvent({
                name: "ui",
                payload: {
                    ui_function: "carousel",
                    ui_action: ui_action,
                    ui_component: "mod-stage_carousel",
                    ui_target: "slide " + (currentSlide - 1 < 0 ? slides.length - 1 : currentSlide - 1),
                },
            });
        }
        setCurrentSlide((currentState) => (currentState - 1 < 0 ? slides.length - 1 : currentState - 1));
    };
    useSwipe({
        left: () => {
            if (disableClickNavigation) {
                return;
            }
            clearInterval(intervalId);
            navigatePrev("prev");
        },
        right: () => {
            if (disableClickNavigation) {
                return;
            }
            clearInterval(intervalId);
            navigateNext("next");
        },
    });
    const handleNavigation = (event) => {
        if (disableClickNavigation) {
            return;
        }
        if (event.target instanceof HTMLElement && event.target.closest("a")) {
            return;
        }
        clearInterval(intervalId);
        if (event.clientX <= window.innerWidth / 2) {
            navigatePrev("prev");
        }
        else {
            navigateNext("next");
        }
    };
    const handleMouseMove = (event) => {
        if (timer) {
            return;
        }
        const target = event.target;
        if (target.closest("a")) {
            setMousePosition(null);
        }
        else {
            setMousePosition({ x: event.clientX + "px", y: event.clientY + "px" });
            setNavigationDirection(event.pageX <= window.innerWidth / 2 ? "prev" : "next");
        }
        setTimer(window.setTimeout(() => {
            setTimer(undefined);
        }, 10));
    };
    const iconIdentifier = navigationDirection === "next" ? "icon-arrow-right" : "icon-arrow-left";
    const variants = {
        enter: (direction) => {
            return {
                x: direction === "next" ? "100%" : "-100%",
            };
        },
        center: {
            zIndex: 1,
            x: 0,
        },
        exit: {
            zIndex: 0,
            x: 0,
        },
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: `relative mx-auto h-screen max-h-[1200px] w-screen max-w-[1920px] overflow-x-hidden p-0 ${slides.length > 1 && !disableClickNavigation ? "cursor-none" : ""}`, onClick: handleNavigation, onMouseMove: handleMouseMove, onMouseLeave: () => {
                setMousePosition(null);
            }, ref: childWrapperRef },
            React.createElement(MotionConfig, { reducedMotion: "user" },
                React.createElement(AnimatePresence, { initial: false, custom: navigationDirection },
                    React.createElement(motion.div, { key: currentSlide, initial: "enter", animate: "center", exit: "exit", custom: navigationDirection, variants: variants, transition: {
                            ease: "easeOut",
                            duration: slideAnimationDuration,
                        }, className: "absolute h-full w-full" },
                        React.createElement(StageCarouselSlide, Object.assign({}, slides[currentSlide]))))),
            React.createElement(AnimatePresence, null, !disableClickNavigation && mousePosition && slides.length > 1 && (React.createElement(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, style: {
                    left: mousePosition.x,
                    top: mousePosition.y,
                }, className: "pointer-events-none fixed z-10 flex h-96 w-96 -translate-x-48 -translate-y-48 items-center justify-center rounded-full bg-black-100 bg-opacity-10 text-white backdrop-blur-3" },
                React.createElement(Icon, { _id: getId(), _name: "IconStruct", className: "before:h-36 before:w-36 before:bg-white", identifier: iconIdentifier })))),
            slides.length > 1 && (React.createElement("div", { className: "container" },
                React.createElement(Indicators, { className: "dark absolute bottom-96 z-10", length: slides.length, activeIndicator: currentSlide }))))));
};
